import useIsMounted from "../../../../../../hooks/use-is-mounted";
import React, {useCallback} from "react";
import {createDateRangeInitialValues, createDateRangeSchema, DateRangeFormSection, prepareDateRangeFilters} from "../form-sections/date-range";
import Api from "../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../core/services/date-utils";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {Card, Typography} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "../../../../../../components/base/button/button";
import ExportingProgress from "../exporting-progress";

export const ExportDataViewSalesTaxForm = ({fetchExportDataWithPagination, exportReport, exporting, progress}) => {
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => {
        const dateRangeSchema = createDateRangeSchema({startDateRequired: true});
        return Yup.object().shape({
            ...dateRangeSchema,
        })
    }, []);

    const initialValues = React.useMemo(() => {
        const dateRangeInitialValues = createDateRangeInitialValues();
        return {
            ...dateRangeInitialValues,
        }
    }, []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Fetches the sales tax data for export
     * @type {(function(*, *): Promise<{hasNext: boolean, data: *[]} | undefined>)}
     */
    const fetchAndPrepareData = useCallback(async (filters, pagination) => {
        const response = await Api.exportSalesTax({
            period: {
                startDateTime: filters.startDate,
                endDateTime: filters.endDate ?? DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
            },
            pagination: pagination,
        });
        if (!isMounted())
            return;
        const length = (response.data?.totalCount ?? 0);
        const hasNext = length > 0 && length > pagination.pageSize * pagination.currentPage;
        return {
            data: response.data?.items?.map((e, index) => ({
                "": (index + 1) + ((pagination.currentPage - 1) * pagination.pageSize),
                'Invoice No': `="${e.invoiceNo ?? ''}"`,
                'Invoice Date': e.invoiceDate ? DateUtils.serverToLocal(e.invoiceDate).format(DateUtils.dateFormat) : '',
                'Required Date': e.requiredDate ? DateUtils.serverToLocal(e.requiredDate).format(DateUtils.dateFormat) : '',
                'Customer': e.customerName ?? '',
                'Total': formatMoney(e.total ?? ''),
                'Sub Total': formatMoney(e.subtotal ?? ''),
                'Freight': formatMoney(e.freight ?? ''),
                'Total Sales Tax': formatMoney(e.totalSalesTax ?? ''),
            })) ?? [],
            hasNext,
            total: length,
        }
    }, [isMounted])

    /**
     * Exports the report based on the selected
     * @type {(function(*, *): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        const filters = {
            ...prepareDateRangeFilters(values),
        }
        await exportReport(async () => {
            const data = await fetchExportDataWithPagination(filters, fetchAndPrepareData, 100)
            return [data, "Sales Taxes"];
        });
    }, [exportReport, fetchExportDataWithPagination, fetchAndPrepareData])


    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3, marginTop: 2}}>
                <Form
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{style: {width: '100%'}}}
                >
                    <Grid2 container spacing={3} justifyContent={'center'}>
                        <Grid2 xs={12} sx={{display: 'flex'}}>
                            <Typography variant={'subtitle1'} align={'center'}>
                                Select Date Range for Sales Tax Report
                            </Typography>
                        </Grid2>
                        <DateRangeFormSection required={required}/>
                        <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBlockStart: 2}}>
                            <Button size={"small"} disabled={exporting} variant={'outlined'} color={'primary'} type={'submit'} sx={{minWidth: 200}}>
                                {exporting ? "Exporting..." : "Export"}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Form>
                {
                    exporting &&
                    <ExportingProgress progress={progress}/>
                }
            </Card>
        </>
    );
}